import {
  ReactToPhaserListeners,
  NotifyReact,
  PhaserListenForReact,
  NotifyReactEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import Piano from '../GameObjects/Piano';
import {
  BaseNote,
  Note,
  getMidiNumberFromBaseNote,
  getMidiNumberFromNote,
} from 'Phaser/Util/OSMDHelper';
import Phaser from 'phaser';
import Instruction from 'Phaser/GameObjects/Instruction';
import ExerciseBase, { IObjective, IConfig } from './ExerciseBase';
import Transition from 'Phaser/Rendering/Transitions';
import { COLORBLIND_TYPE } from 'Utils/Constants';
import { NEXT_RING_FIND_THE_KEY_MS } from 'Utils/playAlong/tutorialPlayAlong';

interface PianoObjective extends IObjective {
  text: string;
  target: 'any' | Note;
  startAfter?: number;
}

export interface Config extends IConfig<PianoObjective> {
  transitionDelay?: number;
  start?: Note;
  end?: Note;
  cbType?: COLORBLIND_TYPE;
  objectives: PianoObjective[];
  transition?: 'Slide' | 'Dissolve';
  textPromptPosition?: 'Top' | 'Bottom';
}

// Scene containing the interactive MIDI piano graphic and its related controllers.
export class PianoScene extends ExerciseBase<PianoObjective, Config> {
  // The piano gameobject
  piano?: Piano;
  // Current octave of the piano (for computer keyboard to MIDI mapping, like in GarageBand)
  octave?: number;

  instruction: Instruction | null = null;

  listening: boolean = false;
  timers: Phaser.Time.TimerEvent[] = [];

  constructor() {
    super({ key: 'PianoScene' });
  }

  create() {
    NotifyReactEvents.CurrentSceneReady(this);
  }

  init(config: Config) {
    console.debug('[tutorial] piano scene init');
    this.currentObjective = -1;
    this.listening = false;
    super.init(config);
  }

  octaveUp() {
    if (this.octave! < this.piano!.highestOctave()) this.octave!++;
  }

  octaveDown() {
    if (this.octave! > this.piano!.lowestOctave()) this.octave!--;
  }

  addKeyToInputs(key: number) {
    return this.input.keyboard?.addKey(key);
  }

  setupKeyboardMapping() {
    /*
    const codes = Phaser.Input.Keyboard.KeyCodes;
    this.addKeyToInputs(codes.Z)?.addListener(
      'down',
      () => this.octaveDown(),
      this
    );
    this.addKeyToInputs(codes.X)?.addListener(
      'down',
      () => this.octaveUp(),
      this
    );
    const map = new Map<number, [BaseNote, number]>();
    map.set(codes.A, ['C', 0]);
    map.set(codes.W, ['C#', 0]);
    map.set(codes.S, ['D', 0]);
    map.set(codes.E, ['D#', 0]);
    map.set(codes.D, ['E', 0]);
    map.set(codes.F, ['F', 0]);
    map.set(codes.T, ['F#', 0]);
    map.set(codes.G, ['G', 0]);
    map.set(codes.Y, ['G#', 0]);
    map.set(codes.H, ['A', 0]);
    map.set(codes.U, ['A#', 0]);
    map.set(codes.J, ['B', 0]);
    map.set(codes.K, ['C', 1]);
    map.set(codes.O, ['C#', 1]);
    map.set(codes.L, ['D', 1]);
    map.set(codes.P, ['D#', 1]);
    map.set(codes.SEMICOLON, ['E', 1]);
    map.set(codes.COMMA, ['F', 1]);
    map.forEach(([baseNote, mod], keyCode) => {
      this.addKeyToInputs(keyCode)?.addListener(
        'down',
        () => {
          this.playNote(baseNote, mod);
        },
        this
      );
      this.addKeyToInputs(keyCode)?.addListener(
        'up',
        () => {
          this.releaseNote(baseNote, mod);
        },
        this
      );
    });
    */
    /*ListenForReactEvent(
      //ListenMidiOn(({ key }: { key: number }) => this.piano!.onPressKey(key)),
      ListenMidiOn(({ key }: { key: number }) => this.midiOn(key)),
      this,
    );
    ListenForReactEvent(
      //ListenMidiOff(({ key }: { key: number }) =>
      //this.piano!.onReleaseKey(key),
      //),
      ListenMidiOff(({ key }: { key: number }) => this.midiOff(key)),
      this,
    );*/
  }

  midiOn({ note }: { note: number; velocity: number }) {
    if (this.listening) this.piano!.onPressKey(note);
  }

  midiOff({ note }: { note: number }) {
    if (this.listening) this.piano!.onReleaseKey(note);
  }

  playNote(base: BaseNote, octaveMod: number = 0) {
    const number = getMidiNumberFromBaseNote(base);
    this.piano!.onPressKey(number + (octaveMod + this.octave!) * 12);
  }

  releaseNote(base: BaseNote, octaveMod: number = 0) {
    const number = getMidiNumberFromBaseNote(base);
    this.piano!.onReleaseKey(number + (octaveMod + this.octave!) * 12);
  }

  initExercise(_objectiveIndex: number): void {
    if (!this.graded) {
      // Ungraded: the target key is lit (nothing for "any key"), and the Next
      // ring runs. No key is listened for: the app cannot hear the player.
      this.listening = false;
      this.dispatchExerciseEvent({ type: 'RUN_ENDED' });
      return;
    }
    this.listening = true;
  }

  protected nextRingMs(): number {
    return NEXT_RING_FIND_THE_KEY_MS;
  }

  pauseScene(): void {
    this.listening = false;
    this.paused = true;
    this.scene.launch('PauseMenu');
    NotifyReactEvents.ExercisePaused({});
  }

  resumeScene(): void {
    this.paused = false;
    if (this.currentObjective !== undefined && this.currentObjective >= 0 && this.hearsNotes()) {
      this.listening = true;
    }
    NotifyReactEvents.ExerciseResumed();
  }

  evaluatePerformance(): void {
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_PASSED' });
  }

  scheduleStop(delay: number = 1500): void {
    this.time.delayedCall(delay, this.stop, [], this);
  }

  loadObjective(objectiveIndex: number): void {
    console.debug('[tutorial] piano scene set objective');
    const nextObjective = (this.currentObjective ?? -1) + 1;

    // Guard: check bounds BEFORE incrementing to avoid leaving state invalid
    if (!this.config || nextObjective >= this.config.objectives.length) {
      console.warn('Piano.setObjective: next objective would be out of bounds, ignoring', {
        currentObjective: this.currentObjective,
        nextObjective,
        objectivesLength: this.config?.objectives?.length,
      });
      return;
    }

    const obj = this.config.objectives[nextObjective];
    if (!obj) {
      console.warn('Piano.setObjective: objective is undefined at index', nextObjective);
      return;
    }

    // Initial setup only runs on first call (when currentObjective is -1 or undefined)
    if (this.currentObjective === undefined || this.currentObjective === -1) {
      const config = this.config;
      this.piano = new Piano(this, 0, 0, config.start ?? 'A0', config.end ?? 'C8', config.cbType);
      this.piano.setScale(0.12);
      this.piano.setPosition(-this.cameras.main.width / 2, this.cameras.main.height / 2 - 25 * 1);
      if (config.transition === 'Dissolve') {
        this.cameras.main.setPostPipeline('Dissolve');
      }
      this.timers.push(
        this.time.delayedCall(
          config.transitionDelay ?? 500,
          () => {
            if (config.transition === 'Dissolve') {
              this.piano!.x = this.cameras.main.width / 2;
              //this.cameras.main.fadeIn(1000);
              //this.cameras.main.alpha = 0;
              this.tweens.add({
                targets: this.cameras.main.getPostPipeline('Dissolve') as Transition,
                progress: 1,
                duration: 1000,
              });
              //this.cameras.main.alpha;
            } else {
              this.tweens.add({
                targets: this.piano,
                x: this.cameras.main.width / 2,
                duration: 1000,
                ease: 'Power1',
              });
            }
          },
          [],
          this,
        ),
      );
      this.setupKeyboardMapping();
      this.octave = 1;
    }

    // Only update currentObjective after validation passes
    this.currentObjective = nextObjective;

    if (obj.startAfter && this.hearsNotes())
      this.time.delayedCall(obj.startAfter * 1000, () => (this.listening = true), [], this);
    this.instruction?.destroy();
    this.instruction = new Instruction(this, obj.text, this.config!.textPromptPosition);

    if (obj.target !== 'any') {
      this.piano?.setValidator((note) => note === getMidiNumberFromNote(obj.target as Note));
      this.piano?.highlightKey(obj.target as Note, true);
    }

    this.piano?.addKeyListener(
      'down',
      obj.target,
      () => {
        if (!this.listening) return;
        if (this.paused) return;
        this.listening = false;
        this.timers.push(this.time.delayedCall(1000, () => this.piano?.clearAllHighlights()));
        this.piano?.removeKeyListener('down', obj.target);
        this.dispatchExerciseEvent({ type: 'PHRASE_END' });
      },
      this,
      false,
    );
    this.dispatchExerciseEvent({ type: 'OBJECTIVE_LOADED' });
  }

  unload() {
    this.timers.forEach((timer) => timer.remove());
    this.timers.clear();
    this.time.removeAllEvents();
    this.piano?.destroy();
    this.removeOwnListener('MidiOff');
    this.removeOwnListener('MidiOn');
    this.removeOwnListener('UpdateObjective');
    super.unload();
  }

  stop() {
    this.scene.stop();
  }

  update() {}
}
